/**
 * Conversations slice for the application store.
 * Handles conversation list, pagination, and search state.
 */

import { defaultPagination } from '../types';

import type { Conversation, PaginationState } from '../types';
import type { StateCreator } from 'zustand';

/**
 * Conversations state and actions.
 */
export interface ConversationsSlice {
  // Sidebar conversations list
  conversations: Conversation[];
  isLoadingConversations: boolean;
  conversationsPagination: PaginationState;

  // Page conversations (for conversations page browsing)
  pageConversations: Conversation[];
  isLoadingPageConversations: boolean;
  pageConversationsPagination: PaginationState;

  // Search state
  conversationSearchResults: Conversation[];
  isSearchingConversations: boolean;
  conversationSearchQuery: string;
  conversationSearchPagination: PaginationState;

  // Sidebar actions
  setConversations: (conversations: Conversation[]) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  removeConversation: (id: string) => void;
  setIsLoadingConversations: (loading: boolean) => void;
  setConversationsPagination: (pagination: PaginationState) => void;

  // Page conversations actions
  setPageConversations: (conversations: Conversation[]) => void;
  setIsLoadingPageConversations: (loading: boolean) => void;
  setPageConversationsPagination: (pagination: PaginationState) => void;

  // Search actions
  setConversationSearchResults: (results: Conversation[]) => void;
  setIsSearchingConversations: (searching: boolean) => void;
  setConversationSearchQuery: (query: string) => void;
  setConversationSearchPagination: (pagination: PaginationState) => void;
  clearConversationSearch: () => void;
}

/**
 * Creates the conversations slice.
 */
export const createConversationsSlice: StateCreator<
  ConversationsSlice,
  [],
  [],
  ConversationsSlice
> = set => ({
  // Initial state - sidebar
  conversations: [],
  isLoadingConversations: false,
  conversationsPagination: defaultPagination,

  // Initial state - page
  pageConversations: [],
  isLoadingPageConversations: false,
  pageConversationsPagination: defaultPagination,

  // Initial state - search
  conversationSearchResults: [],
  isSearchingConversations: false,
  conversationSearchQuery: '',
  conversationSearchPagination: defaultPagination,

  // Sidebar actions
  setConversations: conversations => set({ conversations }),

  addConversation: conversation =>
    set(state => ({
      conversations: [...state.conversations, conversation],
    })),

  updateConversation: (id, updates) =>
    set(state => ({
      conversations: state.conversations.map(conv =>
        conv.id === id ? { ...conv, ...updates } : conv
      ),
    })),

  removeConversation: id =>
    set(state => ({
      conversations: state.conversations.filter(conv => conv.id !== id),
    })),

  setIsLoadingConversations: loading => set({ isLoadingConversations: loading }),

  setConversationsPagination: pagination => set({ conversationsPagination: pagination }),

  // Page conversations actions
  setPageConversations: conversations => set({ pageConversations: conversations }),

  setIsLoadingPageConversations: loading => set({ isLoadingPageConversations: loading }),

  setPageConversationsPagination: pagination => set({ pageConversationsPagination: pagination }),

  // Search actions
  setConversationSearchResults: results => set({ conversationSearchResults: results }),

  setIsSearchingConversations: searching => set({ isSearchingConversations: searching }),

  setConversationSearchQuery: query => set({ conversationSearchQuery: query }),

  setConversationSearchPagination: pagination => set({ conversationSearchPagination: pagination }),

  clearConversationSearch: () =>
    set({
      conversationSearchResults: [],
      isSearchingConversations: false,
      conversationSearchQuery: '',
      conversationSearchPagination: defaultPagination,
    }),
});
